import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { GroupedRows, NumberPortabilitiesContext } from '../check-number-portability/number-portabilities.context';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ButtonSizes, ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import {
  DnDidRange,
  MicrosoftHelpdeskOptions,
  PortInOrderDraft,
  PortInOrderDraftRef,
  PortType,
} from '../../../shared/models/generated/ziroModels';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { forkJoin, Observable, Subscription, switchMap, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { TelephoneNumberFilter } from '../../../shared/filters/telephone-number.filter';
import { cloneDeep } from 'lodash';
import { PortabilityRow } from '../check-number-portability/check-number-portability.component';
import { HtmlInputType, ZiroTextConfig } from '../../../forms/fields/text/ziro-text.component';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { ZiroSelectConfig } from '../../../forms/fields/select/ziro-select.component';
import { ZiroCheckboxFieldConfig } from '../../../shared/custom-configs/ziro-checkbox-config/ziro-checkbox-config.component';
import { CustomWeekDay, DateComponentConfig } from '../../../forms/fields/date/date.component';
import { groupedProvinces, groupedStates } from '../order-view-and-edit/order-view-and-edit.component';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { HelpdeskOptionsContext } from '../../../helpdesk/shared/contexts/helpdesk-options.context';
import { PortInDraftsResource } from '../port-in-orders/port-in-drafts.resource';
import { ToastService } from '../../../shared/services/toast.service';
import { catchError, tap } from 'rxjs/operators';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { PortInOrdersResource } from '../port-in-orders/port-in-orders.resource';
import { provinces } from '../order-view-and-edit/state-province-options';
import { DatatableColumn, DatatableRow } from '../../../shared/tables/datatable/datatable.models';
import { CucmDnReportRow } from '../../../migrations/migrations-cisco/migrations-cisco-directory-number-report/migrations-cisco-directory-numbers-report.component';

export interface CreateOrderDraftRow extends DatatableRow {
  losingCarrier: string;
  numberRanges: string[];
}

@Component({
  selector: 'ziro-create-draft',
  templateUrl: './create-draft.component.html',
  styleUrls: ['../../admin-page.scss'],
  providers: [PortInOrdersResource],
  standalone: false,
})
export class CreateDraftComponent extends ZiroFormAbstractDirective<PortInOrderDraft> implements OnInit, OnDestroy {
  protected _ziroFormStateService: ZiroFormStateService;
  private _numberPortabilityContext = inject(NumberPortabilitiesContext);
  private _bottomNavService = inject(BottomNavService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _translateService = inject(TranslateService);
  private _router = inject(Router);
  private _phoneNumberFilter = inject(TelephoneNumberFilter);
  private _helpdeskOptionsContext = inject(HelpdeskOptionsContext);
  private _toastService = inject(ToastService);
  private _portInOrdersResource = inject(PortInOrdersResource);
  private _portInDraftsResource = inject(PortInDraftsResource);

  buttonSizes = ButtonSizes;
  buttonStyles = ButtonStyles;
  ziroIcons = ZiroIcons;
  isLoading = true;
  showNoPortsAlert = false;
  formConfig: ZiroFormConfig;
  columns: DatatableColumn<CreateOrderDraftRow>[] = [
    {
      name: 'losingCarrier',
      label: 'tkey;admin.order_numbers.create_orders.datatable.losing_carrier.label',
    },
    {
      name: 'numberRanges',
      label: 'tkey;admin.order_numbers.create_orders.datatable.numbers.label',
      sortFn: (a: DnDidRange, b: DnDidRange) => {
        const aRange: DnDidRange = a || { start: '0', end: '' };
        const bRange: DnDidRange = b || { start: '0', end: '' };
        return parseInt(aRange.start) - parseInt(bRange.start);
      },
    },
  ];
  rows: CreateOrderDraftRow[] = [];

  private _zipValidationParams = '';
  private _companyId = '';
  private _groupedRows: GroupedRows;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this._breadcrumbsService.updateBreadcrumbs([
      {
        label: 'tkey;admin.order_numbers.port_in_orders.title',
        url: '/app2/#/admin/order-numbers/port-in-orders',
      },
      {
        label: 'tkey;admin.order_numbers.check_number_portability.title',
        url: '/app2/#/admin/order-numbers/port-in-drafts/check-number-portability',
      },
      { label: 'tkey;admin.order_numbers.create_orders.title' },
    ]);

    const numberPortabilitySub = this._numberPortabilityContext.groupedRowsState$.subscribe(
      (groupedRows: GroupedRows) => {
        this._groupedRows = groupedRows;
        const formattedNumberRows = this._formatNumbers(cloneDeep(groupedRows));
        this.rows = this._getTableRows(formattedNumberRows);
        this.showNoPortsAlert = this._getNumberOfPossiblePorts() === 0 || Object.keys(groupedRows).length === 0;
        this._initForm();
        this._refreshBottomNav();
        this.isLoading = false;
      },
    );
    const helpdeskOptionsSub = this._helpdeskOptionsContext.state$.subscribe((options: MicrosoftHelpdeskOptions) => {
      this._companyId = options.companyId;
    });
    const formSubmittedSub = this._validateAndSubmitSource.subscribe(() => {
      this._refreshBottomNav(true);
      if (!this.isFormValid()) {
        this._bottomNavService.dispatch(
          new BottomNavUpdateState({
            hasValidationError: !this.isFormValid(),
          }),
        );
        this._refreshBottomNav();
      }
    });
    const formUpdateSub = this.ziroFormsUpdate$.subscribe(() => {
      if (this.isFormValid()) {
        this._bottomNavService.dispatch(
          new BottomNavUpdateState({
            hasValidationError: !this.isFormValid(),
          }),
        );
      }
    });
    this._subscriptions.add(numberPortabilitySub);
    this._subscriptions.add(helpdeskOptionsSub);
    this._subscriptions.add(formSubmittedSub);
    this._subscriptions.add(formUpdateSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  submit(): Observable<PortInOrderDraftRef[]> {
    return this._createDrafts();
  }

  onRowSelected(): void {
    this._refreshBottomNav();
  }

  private _formatNumbers(groupedRows: GroupedRows): GroupedRows {
    for (const groupedRow in groupedRows) {
      groupedRows[groupedRow].numbers = groupedRows[groupedRow].numbers.map((number) =>
        this._phoneNumberFilter.transform(number.startsWith('+1') ? number : `+1 ${number}`),
      );
    }
    return groupedRows;
  }

  private _getTableRows(formattedNumberRows: GroupedRows): CreateOrderDraftRow[] {
    return Object.values(formattedNumberRows).map((row: PortabilityRow) => {
      const disabledTooltip = !row.rateCenterSupported
        ? this._translateService.instant(
            'tkey;admin.order_numbers.create_orders.datatable.rate_center_not_supported.tooltip',
            { value: row.rateCenter },
          )
        : !row.losingCarrierSupported
          ? this._translateService.instant(
              'tkey;admin.order_numbers.create_orders.datatable.carrier_not_supported.tooltip',
              { value: row.losingCarrier },
            )
          : '';

      let losingCarrierHtml: string = null;
      if (row.portType === PortType.MIXED || row.csrRetrievalSupported) {
        losingCarrierHtml = row.losingCarrier;
        if (row.portType === PortType.MIXED) {
          losingCarrierHtml += ` <i class="${this.ziroIcons.WARNING_TRIANGLE} text-warning"></i>`;
        }
        if (row.csrRetrievalSupported) {
          losingCarrierHtml += `<br><span class="badge bg-success">${this._translateService.instant(
            'tkey;admin.order_numbers.create_orders.datatable.csr_retrieval_supported',
          )}</span>`;
        }
      }

      return {
        losingCarrier: row.losingCarrier,
        numberRanges: row.numbers,
        isTableRowSelectable: true,
        isSelectedInTable: row.losingCarrierSupported && row.rateCenterSupported,
        isTableRowSelectDisabled: !row.losingCarrierSupported || !row.rateCenterSupported,
        tableRowSelectDisabledTooltip: disabledTooltip,
        cssClass: !row.losingCarrierSupported || !row.rateCenterSupported ? 'disabled' : '',
        hardcodedTooltips: {
          losingCarrier:
            row.portType === PortType.MIXED
              ? 'tkey;admin.order_numbers.create_orders.portability_warning.mixed_port_type'
              : '',
        },
        hardcodedDisableTooltip: {
          losingCarrier: row.portType !== PortType.MIXED,
        },
        badges: {
          numberRanges: {
            values: row.numbers.map((number) => {
              return { label: number };
            }),
          },
        },
        html: {
          losingCarrier: losingCarrierHtml,
        },
      };
    });
  }

  private _refreshBottomNav(pending = false): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'cancel-button',
          dataAutomation: 'cancel-button',
          label: 'tkey;global.button.cancel.text',
          buttonClass: ButtonStyles.DEFAULT,
          icon: ZiroIcons.NONE,
          cb: () => {
            this._router.navigateByUrl('/admin/order-numbers/port-in-drafts/check-number-portability');
          },
        },
        {
          id: 'create-drafts-button',
          dataAutomation: 'create-drafts-button',
          label: this._translateService.instant('tkey;admin.order_numbers.create_orders.create_drafts_button.text', {
            value: this._getNumberOfSelectedPorts().toString(),
          }),
          buttonClass: ButtonStyles.PRIMARY,
          state: {
            pending: pending,
            tooltipVisible: this._getNumberOfSelectedPorts() === 0,
            buttonDisableState: {
              disabled: this._getNumberOfSelectedPorts() === 0,
              tooltipKey: 'tkey;admin.order_numbers.create_orders.create_draft.button_disabled.tooltip',
            },
          },
          icon: ZiroIcons.SAVE_SUCCESS,
          submitSubject: this._validateAndSubmitSource,
          type: ButtonTypes.SUBMIT,
        },
      ]),
    );
  }

  private _createDrafts(): Observable<PortInOrderDraftRef[]> {
    return forkJoin([this._portInDraftsResource.get(), this._portInOrdersResource.get()]).pipe(
      switchMap(([drafts, orders]) => {
        let orderIdNumber = 1;
        const filteredItems = [...drafts, ...orders].filter(
          (item) =>
            item.customerOrderId &&
            item.customerOrderId.toLowerCase().includes(this.entity.customerOrderId.toLowerCase()),
        );
        if (filteredItems.length) {
          const mappedNums = filteredItems.map((order) => {
            return order.customerOrderId.split(this.entity.customerOrderId).pop();
          });
          if (mappedNums.length) {
            const intArray = mappedNums.map((id) => parseInt(id, 10)).filter((num) => !isNaN(num));
            if (intArray.length) {
              orderIdNumber = Math.max(...intArray) + 1;
            }
          }
        }

        const allDraftObs$: Observable<PortInOrderDraftRef>[] = Object.values(this._groupedRows)
          .filter((row) => row.rateCenterSupported && row.losingCarrierSupported)
          .map((groupedRow: PortabilityRow) => {
            const customerOrderId = `${this._companyId} ${this.entity.customerOrderId} ${orderIdNumber}`;
            const draft: PortInOrderDraft = {
              customerOrderId,
              tollFree: groupedRow.portType === PortType.MANUALTOLLFREE,
              losingCarrierName: groupedRow.losingCarrier,
              phoneNumbers: groupedRow.numbers,
              rateCenterName: groupedRow.rateCenter,
              desiredFocDate: this.formData.desiredFocDate ?? '',
              accountNumber: this.formData.accountNumber ?? '',
              addressLine2: this.formData.addressLine2 ?? '',
              businessName: this.formData.businessName ?? '',
              city: this.formData.city ?? '',
              country: provinces.map((prov) => prov.value).includes(this.formData.stateCode)
                ? 'Canada'
                : 'United States',
              draftId: null,
              houseNumber: this.formData.houseNumber ?? '',
              loaAuthorizingPerson: this.formData.loaAuthorizingPerson ?? '',
              billingTelephoneNumber: this.formData.billingTelephoneNumber
                ? '+1'.concat(this.formData.billingTelephoneNumber)
                : '',
              newBillingTelephoneNumber: this.formData.newBillingTelephoneNumber
                ? this.formData.newBillingTelephoneNumber !== ''
                  ? '+1'.concat(this.formData.newBillingTelephoneNumber)
                  : ''
                : '',
              partialPort: this.formData.partialPort || false,
              pinNumber: this.formData.pinNumber ?? '',
              stateCode: this.formData.stateCode ?? '',
              streetName: this.formData.streetName ?? '',
              zip: this.formData.zip,
            };
            orderIdNumber++;
            return this._portInDraftsResource.post(draft).pipe(
              tap(() => {
                this._toastService.push(
                  ToastTypes.SUCCESS,
                  ZiroIcons.SAVE_SUCCESS,
                  'tkey;admin.order_numbers.port_in_drafts.title',
                  this._translateService.instant('tkey;admin.order_numbers.create_orders.create_draft.success', {
                    orderId: customerOrderId,
                  }),
                );
              }),
            );
          });
        return forkJoin([...allDraftObs$]).pipe(
          catchError((err) => {
            this._refreshBottomNav();
            return throwError(() => err);
          }),
          tap(() => this._router.navigateByUrl('/admin/order-numbers/port-in-orders')),
        );
      }),
    );
  }

  private _initForm(): void {
    this.formConfig = {
      fields: {
        customerOrderId: {
          label: 'tkey;admin.order_numbers.create_orders.create_draft.site_form.site_field.label',
          dataAutomation: 'siteName',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.TEXT,
          }),
          required: true,
          helpText: 'tkey;admin.order_numbers.create_orders.create_draft.site_form.site_field.helptext',
          validation: [
            {
              validator: (val: string) => {
                return !val || val.length <= 30 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
              },
              message: {
                content: 'tkey;global.validation.max_length',
                params: {
                  maxLength: '30',
                },
              },
            },
          ],
        },
        loaAuthorizingPerson: {
          label: 'tkey;admin.order_numbers.port_in_drafts_edit.form.authorizing_person.label',
          dataAutomation: 'loaAuthorizingPerson',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          required: true,
          helpText: 'tkey;admin.order_numbers.create_orders.create_draft.site_form.loa_field.helptext',
          validation: [
            {
              validator: (val: string) => {
                return !val || val.length <= 15 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
              },
              message: {
                content: 'tkey;global.validation.max_length',
                params: {
                  maxLength: '15',
                },
              },
            },
          ],
        },
        businessName: {
          label: 'tkey;admin.order_numbers.port_in_drafts_edit.form.business_name.label',
          dataAutomation: 'businessName',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          required: true,
          validation: [
            {
              validator: (val: string) => {
                return !val || val.length <= 50 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
              },
              message: {
                content: 'tkey;global.validation.max_length',
                params: {
                  maxLength: '50',
                },
              },
            },
          ],
        },
        houseNumber: {
          label: 'tkey;admin.order_numbers.port_in_drafts_edit.form.street_number.label',
          dataAutomation: 'houseNumber',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          required: true,
        },
        streetName: {
          label: 'tkey;admin.order_numbers.port_in_drafts_edit.form.street_name.label',
          dataAutomation: 'streetName',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          required: true,
        },
        addressLine2: {
          label: 'tkey;admin.order_numbers.port_in_drafts_edit.form.address_line_2.label',
          dataAutomation: 'addressLine2',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
        },
        city: {
          label: 'tkey;admin.order_numbers.port_in_drafts_edit.form.city.label',
          dataAutomation: 'city',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          required: true,
        },
        stateCode: {
          label: 'tkey;admin.order_numbers.port_in_drafts_edit.form.state.label',
          dataAutomation: 'stateCode',
          componentConfig: new ZiroSelectConfig({
            options: [...groupedStates, ...groupedProvinces],
            bindValue: 'value',
          }),
          required: true,
        },
        country: {
          label: '',
          dataAutomation: 'country',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          hidden: () => true,
        },
        zip: {
          label: 'tkey;admin.order_numbers.port_in_drafts_edit.form.zip.label',
          dataAutomation: 'zip',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          required: true,
          validation: [
            {
              validator: (val: string, stateCode: string) => {
                if (provinces.map((prov) => prov.value).includes(stateCode)) {
                  const postalRegex = /^([A-Za-z][0-9][A-Za-z])[\s-]?([0-9][A-Za-z][0-9])$/;
                  this._zipValidationParams = 'Postal Code';
                  return postalRegex.test(val) ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
                } else {
                  const zipRegex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
                  this._zipValidationParams = 'ZIP';
                  return zipRegex.test(val) ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
                }
              },
              message: () => {
                return {
                  content: 'tkey;admin.order_numbers.port_in_drafts_edit.form.zip.validation.message',
                  params: {
                    type: this._zipValidationParams,
                  },
                };
              },
              injectValuesFromFields: ['stateCode'],
            },
          ],
        },
        billingTelephoneNumber: {
          label: 'tkey;admin.order_numbers.port_in_drafts_edit.form.billing_telephone_number.label',
          dataAutomation: 'billingTelephoneNumber',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          helpText: 'tkey;admin.order_numbers.port_in_drafts_edit.form.billing_telephone_number.helptext',
          required: true,
          validation: [
            {
              validator: (val: string) => {
                return !val || val.length === 10 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
              },
              message: {
                content: 'tkey;global.validation.max_length',
                params: {
                  maxLength: '10',
                },
              },
            },
          ],
        },
        accountNumber: {
          label: 'tkey;admin.order_numbers.port_in_drafts_edit.form.account_number.label',
          dataAutomation: 'account-number',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          validation: [
            {
              validator: (val: string) => {
                return !val || val.length <= 15 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
              },
              message: {
                content: 'tkey;global.validation.max_length',
                params: {
                  maxLength: '15',
                },
              },
            },
          ],
        },
        pinNumber: {
          label: 'tkey;admin.order_numbers.port_in_drafts_edit.form.account_pin.label',
          dataAutomation: 'account-pin',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          validation: [
            {
              validator: (val: string) => {
                return !val || val.length <= 20 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
              },
              message: {
                content: 'tkey;global.validation.max_length',
                params: {
                  maxLength: '20',
                },
              },
            },
          ],
        },
        partialPort: {
          label: 'tkey;admin.order_numbers.port_in_drafts_edit.form.partial_port.label',
          dataAutomation: 'partialPort',
          componentConfig: new ZiroCheckboxFieldConfig(),
          helpText: 'tkey;admin.order_numbers.port_in_drafts_edit.form.partial_port.helptext',
        },
        newBillingTelephoneNumber: {
          label: 'tkey;admin.order_numbers.port_in_drafts_edit.form.replacement_billing_telephone_number.label',
          dataAutomation: 'newBillingTelephoneNumber',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          helpText: 'tkey;admin.order_numbers.port_in_drafts_edit.form.replacement_billing_telephone_number.helptext',
          hidden: () => !this.formData?.partialPort,
          validation: [
            {
              validator: (val: string) => {
                return !val || (val.startsWith('+1') && val.length === 12)
                  ? ZiroFormsValidationState.VALID
                  : ZiroFormsValidationState.INVALID;
              },
              message: 'tkey;validators.global.e164.invalid.error',
            },
          ],
        },
        desiredFocDate: {
          label: 'tkey;admin.order_numbers.port_in_drafts_edit.form.desired_foc_date.label',
          dataAutomation: 'desiredFocDate',
          componentConfig: new DateComponentConfig({
            disabledDays: [CustomWeekDay.Saturday, CustomWeekDay.Sunday],
            allowToday: false,
            allowBeforeToday: false,
          }),
          required: true,
          helpText: 'tkey;admin.order_numbers.port_in_drafts_edit.form.desired_foc_date.helptext',
        },
      },
    };
  }

  private _getNumberOfSelectedPorts(): number {
    return this.rows.filter((row: CucmDnReportRow) => row.isSelectedInTable).length;
  }

  private _getNumberOfPossiblePorts(): number {
    return this.rows.filter((row: CucmDnReportRow) => !row.isTableRowSelectDisabled).length;
  }
}
